/**
 * The life of one cost confirmation request, owned above the dialog so closing the dialog never loses it
 * (batch 8 T11, #3032). See `pendingOperation.ts` for the contract: the exact request and key are written
 * down before sending, an uncertain result keeps them, and the only way out of "unknown" is to send the
 * identical request again or to discard it knowingly. A fresh key is never created for a request that may
 * already have committed. Who may retry, and when a late answer may count, is `useKeyedIntent`'s: the
 * request belongs to the signed-in session and is fenced by its current permission.
 */
import { useCallback, useMemo } from 'react';
import { useConfirmOpeningCosts, type ConfirmOutcome, type ConfirmRequest } from '@/hooks/useInventoryCosting';
import {
  clearPending,
  dropUnusablePending,
  loadPending,
  newOperationKey,
  savePending,
  type PendingConfirm,
  type PersistenceStatus,
} from './pendingOperation';
import { useKeyedIntent, type FlowPhase, type IntentAdapter } from './useKeyedIntent';

export type { FlowPhase } from './useKeyedIntent';

export interface ConfirmFlow {
  readonly phase: FlowPhase;
  /** The sent request whose result is not known (or is being sent). */
  readonly pending: PendingConfirm | null;
  /** `unavailable`: the browser could not keep the request, so a reload cannot resend it. */
  readonly persistence: PersistenceStatus | null;
  /** The pending request was found in storage after a reload, not sent by this page. */
  readonly restored: boolean;
  /** A stored request that could not be used (unscoped old record, damaged): reported, never resent. */
  readonly discardedStored: boolean;
  /** The browser would not let stored requests be read: one may exist that this page cannot see. */
  readonly storageUnreadable: boolean;
  /** The server read the last request and refused it; nothing was written. */
  readonly refusal: unknown;
  readonly outcome: ConfirmOutcome | null;
  start(request: ConfirmRequest): Promise<ConfirmOutcome | null>;
  retry(): Promise<ConfirmOutcome | null>;
  discard(): void;
  dismissRefusal(): void;
  dismissOutcome(): void;
  dismissDiscardedStored(): void;
}

export function useConfirmFlow(): ConfirmFlow {
  const mutation = useConfirmOpeningCosts();
  const send = mutation.mutateAsync;

  const adapter = useMemo<IntentAdapter<PendingConfirm, ConfirmOutcome>>(
    () => ({
      kind: 'confirm',
      load: loadPending,
      save: savePending,
      clear: clearPending,
      dropUnusable: dropUnusablePending,
      send: (intent) => send({ key: intent.key, request: intent.request, scope: intent.scope }),
    }),
    [send],
  );
  const intent = useKeyedIntent(adapter);
  const { begin, retry, discard, dismissRefusal, dismissOutcome, dismissDiscarded } = intent;

  const start = useCallback(
    (request: ConfirmRequest) => begin((scope) => ({ key: newOperationKey(), scope, request, startedAt: new Date().toISOString() })),
    [begin],
  );

  return {
    phase: intent.phase,
    pending: intent.intent,
    persistence: intent.persistence,
    restored: intent.restored,
    discardedStored: intent.discarded,
    storageUnreadable: intent.storageUnreadable,
    refusal: intent.refusal,
    outcome: intent.outcome,
    start,
    retry,
    discard,
    dismissRefusal,
    dismissOutcome,
    dismissDiscardedStored: dismissDiscarded,
  };
}
